import { stockLedger } from "./stockLedger";
// Pure calculation & formatting helpers extracted from ExcelApp.jsx (stateless).
export const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
export const safeDiv = (a, b) => (b === 0 ? 0 : a / b);
export const euro = (v) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(toNum(v));
export const kgf = (v) => new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 1 }).format(toNum(v)) + " kg";
export const pct = (v) => new Intl.NumberFormat("nl-NL", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(toNum(v));
// Aantal stuks per doos uit de productnaam, bv. "Gegrilde halve kip (16st)" -> 16
export function stuksPerDoos(naam) {
  const m = String(naam || "").match(/(\d+)\s*st\b/i);
  return m ? Number(m[1]) : 0;
}
// Effectieve verkoopprijs per kg. Voor "per doos"-producten afgeleid uit doosprijs / (stuks x gewicht/stuk).
export function verkoopPerKg(p) {
  if (p && p.perDoos) {
    const kgPerDoos = stuksPerDoos(p.naam) * ((toNum(p.gramPerStuk) || 500) / 1000);
    return kgPerDoos > 0 ? toNum(p.doosprijs) / kgPerDoos : 0;
  }
  return toNum(p && p.verkoopprijs);
}
// Marge-info per product. Voor "per doos" op doosbasis, anders per kg. margeP is eenheid-onafhankelijk.
export function margeInfo(p, kpPerKg) {
  const kp = toNum(kpPerKg);
  if (p && p.perDoos) {
    const stuks = stuksPerDoos(p.naam);
    const kgPerDoos = stuks * ((toNum(p.gramPerStuk) || 500) / 1000);
    const doos = toNum(p.doosprijs);
    const heeftVp = doos > 0 && kgPerDoos > 0;
    const kostDoos = kp * kgPerDoos;
    return { perDoos: true, eenheid: "doos", stuks, kgPerDoos, heeftVp, vpKg: kgPerDoos > 0 ? doos / kgPerDoos : 0, verkoopDisp: doos, kostDisp: kostDoos, margeE: heeftVp ? doos - kostDoos : null, margeP: heeftVp ? (doos - kostDoos) / doos : null };
  }
  const v = toNum(p && p.verkoopprijs);
  const heeftVp = v > 0;
  return { perDoos: false, eenheid: "kg", stuks: 0, kgPerDoos: 0, heeftVp, vpKg: v, verkoopDisp: v, kostDisp: kp, margeE: heeftVp ? v - kp : null, margeP: heeftVp ? (v - kp) / v : null };
}
export const getYear = (s) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? 0 : d.getFullYear(); };
export function getISOWeek(s) {
  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return 0;
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}
export const isCoated = (e) => (
  String(e.predust || "").trim() !== "" || String(e.battermix || "").trim() !== "" || String(e.breader || "").trim() !== "" ||
  toNum(e.predustKg) > 0 || toNum(e.battermixKg) > 0 || toNum(e.breaderKg) > 0
);
export function calculate(entry, costs) {
  const geproduceerdKg = toNum(entry.geproduceerdKg);
  const uitvalKg = toNum(entry.uitvalKg);
  const goedProductKg = entry.goedProductKg === "" || entry.goedProductKg == null ? Math.max(geproduceerdKg - uitvalKg, 0) : toNum(entry.goedProductKg);
  const productieUren = toNum(entry.productieUren);
  const stillstandUren = toNum(entry.stillstandUren);
  const nettoProductieUren = Math.max(productieUren - stillstandUren, 0);
  const manuren = toNum(entry.aantalMensen) * productieUren;
  const beschikbaarheid = safeDiv(nettoProductieUren, productieUren);
  const prestatie = safeDiv(safeDiv(geproduceerdKg, nettoProductieUren), toNum(entry.normKgUur));
  const kwaliteit = safeDiv(goedProductKg, geproduceerdKg);
  const oee = beschikbaarheid * prestatie * kwaliteit;
  const arbeidskosten = manuren * toNum(costs.uurtarief);
  const grondstofkosten = Math.max(toNum(entry.grondstofKg) - toNum(entry.restantKg), 0) * toNum(entry.prijsGrondstofKg);
  const predustKosten = toNum(entry.predustKg) * toNum(entry.predustPrijsKg);
  const battermixKosten = toNum(entry.battermixKg) * toNum(entry.battermixPrijsKg);
  const breaderKosten = toNum(entry.breaderKg) * toNum(entry.breaderPrijsKg);
  const hamkaasKosten = toNum(entry.hamkaasKg) * toNum(entry.hamkaasPrijsKg);
  // Energie en schoonmaak (water/reiniging) worden apart getoond als info, maar NIET meegeteld
  // in de totale kosten/kostprijs (deze zitten al verwerkt in de vaste kosten binnen "Overig").
  const energieKosten = geproduceerdKg * toNum(costs.energiePerKg);
  const schoonmaakKosten = geproduceerdKg * toNum(costs.waterReinigingPerKg);
  const stikstofKosten = geproduceerdKg * toNum(costs.stikstofPerKg);
  const overigBruto = geproduceerdKg * toNum(entry.overigPerKg);
  const overigKosten = Math.max(overigBruto - schoonmaakKosten, 0);
  const gecoat = isCoated(entry);
  const looptDoorFrituurolie = entry.looptDoorFrituurolie === true;
  const gaatDoorFrituurolie = gecoat || looptDoorFrituurolie;
  const sojaolieKosten = gaatDoorFrituurolie ? geproduceerdKg * toNum(costs.sojaoliePerKg) : 0;
  const hulpstofKosten = predustKosten + battermixKosten + breaderKosten + hamkaasKosten + sojaolieKosten;
  const totaleKosten = arbeidskosten + grondstofkosten + hulpstofKosten + stikstofKosten + overigKosten;
  return {
    ...entry, jaar: getYear(entry.datum), weeknr: getISOWeek(entry.datum), geproduceerdKg, uitvalKg, goedProductKg,
    nettoProductieUren, manuren, kgPerManuur: safeDiv(geproduceerdKg, manuren), uitvalPct: safeDiv(uitvalKg, geproduceerdKg),
    beschikbaarheid, prestatie, kwaliteit, oee, arbeidskosten, grondstofkosten, predustKosten, battermixKosten,
    breaderKosten, hamkaasKosten, hulpstofKosten, energieKosten, schoonmaakKosten, stikstofKosten, overigKosten, sojaolieKosten,
    totaleKosten, kostprijsPerKg: safeDiv(totaleKosten, geproduceerdKg), isGecoat: gecoat,
    looptDoorFrituurolie, gaatDoorFrituurolie,
  };
}
export const sum = (rows, fn) => rows.reduce((t, r) => t + toNum(fn(r)), 0);
export const tellingWeekSummary = (list) => {
  const m = new Map();
  (list || []).forEach((t) => {
    const jaar = getYear(t.datum); const week = getISOWeek(t.datum);
    const key = `${jaar}-${String(week).padStart(2, "0")}`;
    const g = m.get(key) || { key, jaar, week, aantal: 0, geteld: 0, systeem: 0, verschil: 0 };
    g.aantal += 1; g.geteld += toNum(t.geteld); g.systeem += toNum(t.theoretisch); g.verschil += toNum(t.verschil);
    m.set(key, g);
  });
  return [...m.values()].sort((a, b) => (a.key < b.key ? 1 : -1));
};
export const avg = (rows, fn) => (rows.length === 0 ? 0 : sum(rows, fn) / rows.length);
const _STOP = new Set(["kip", "de", "het", "een", "en", "van", "met", "gr", "gram", "kg", "stuks", "stuk", "per", "vers", "iqf", "tk", "bulk", "blanco", "zak", "doos", "krat"]);
const _tok = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").split(/\s+/).filter((w) => w.length > 1);
const _tokWeight = (w) => (/\d/.test(w) ? 0.15 : (_STOP.has(w) ? 0.3 : 1));
export function stringSim(a, b) {
  const A = _tok(a), Bset = new Set(_tok(b));
  if (!A.length || !Bset.size) return 0;
  const seen = new Set();
  let inter = 0, union = 0;
  A.forEach((w) => { if (seen.has(w)) return; seen.add(w); const wt = _tokWeight(w); union += wt; if (Bset.has(w)) inter += wt; });
  _tok(b).forEach((w) => { if (seen.has(w)) return; seen.add(w); union += _tokWeight(w); });
  return union === 0 ? 0 : inter / union;
}
export function bestMatchName(name, names) { let best = "", score = -1; names.forEach((n) => { const s = stringSim(name, n); if (s > score) { score = s; best = n; } }); return score > 0 ? best : ""; }


const _arr = (a) => (Array.isArray(a) ? a.filter((x) => x && typeof x === "object") : []);
// Voegt hulpstoffen met hetzelfde artikelnummer samen tot één regel. De naam van de
// als eerste aangemaakte hulpstof (laagste id) wordt als hoofdnaam gebruikt; kg's en
// voorraadwaarde worden bij elkaar opgeteld. Zonder artikelnummer blijft elke naam apart.
export function groupHelpers(helpers) {
  const arr = _arr(helpers).filter((h) => h.naam);
  const byKey = new Map();
  arr.forEach((h) => {
    const art = String(h.artikelnr || "").trim().toLowerCase();
    const key = art ? `art|${art}` : `naam|${String(h.naam).trim().toLowerCase()}`;
    const g = byKey.get(key) || { key, artikelnr: String(h.artikelnr || "").trim(), members: [] };
    g.members.push(h);
    byKey.set(key, g);
  });
  return [...byKey.values()].map((g) => {
    const sorted = [...g.members].sort((a, b) => toNum(a.id) - toNum(b.id));
    const canon = sorted[0];
    const lev = canon.leverancier || (sorted.find((m) => m.leverancier) || {}).leverancier || "";
    return {
      key: g.key, artikelnr: g.artikelnr, naam: canon.naam, type: canon.type || "", canon,
      namen: [...new Set(sorted.map((m) => m.naam))], memberIds: sorted.map((m) => m.id), leverancier: lev, prijs: toNum(canon.prijs),
      minVoorraad: sorted.reduce((mx, m) => Math.max(mx, toNum(m.minVoorraad)), 0),
      correctie: sorted.reduce((s, m) => s + toNum(m.correctie), 0),
    };
  });
}
export function computeStockList(data) {
  const d = data || {};
  const rawLedger = stockLedger(d, "Grondstof"), helperLedger = stockLedger(d, "Hulpstof");
  const entries = _arr(d.entries), expeditie = _arr(d.expeditie), hulpInk = _arr(d.hulpstofInkoop), runs = _arr(d.halffabRuns), afk = _arr(d.afkeur);
  const afkKg = (cat, naam) => afk.reduce((s, a) => s + (!a.teruggedraaid && a.categorie === cat && a.naam === naam ? toNum(a.kg) : 0), 0);
  const grondVerbruik = (naam) => entries.reduce((s, e) => s + (e.grondstof === naam ? Math.max(toNum(e.grondstofKg) - toNum(e.restantKg), 0) : 0), 0) + runs.reduce((s, r) => s + _arr(r.verbruik).reduce((a, v) => a + (v.categorie === "Grondstof" && v.naam === naam ? toNum(v.kg) : 0), 0), 0) + afkKg("Grondstof", naam);
  const hulpVerbruik = (naam) => entries.reduce((s, e) => s + (e.predust === naam ? toNum(e.predustKg) : 0) + (e.battermix === naam ? toNum(e.battermixKg) : 0) + (e.breader === naam ? toNum(e.breaderKg) : 0) + (e.hamkaas === naam ? toNum(e.hamkaasKg) : 0), 0) + runs.reduce((s, r) => s + _arr(r.verbruik).reduce((a, v) => a + (v.categorie === "Hulpstof" && v.naam === naam ? toNum(v.kg) : 0), 0), 0) + afkKg("Hulpstof", naam);
  const grond = _arr(d.rawMaterials).filter((m) => m.naam).map((m) => { const ontv = expeditie.reduce((s, b) => s + (b.grondstof === m.naam ? toNum(b.kg) : 0), 0); const vr = rawLedger.lots.filter((l) => l.names.includes(m.naam)).reduce((s, l) => s + l.rest, 0); const min = toNum(m.minVoorraad); return { cat: "Grondstof", key: `Grondstof|${m.naam}`, naam: m.naam, artikelnr: m.artikelnummer || "", leverancier: m.leverancier || "", type: "", voorraad: vr, min, laag: min > 0 && vr < min }; });
  const hulp = groupHelpers(d.helpers).map((g) => { const ontv = g.namen.reduce((s, nm) => s + hulpInk.reduce((a, b) => a + (b.hulpstof === nm ? toNum(b.kg) : 0), 0), 0); const vr = helperLedger.lots.filter((l) => l.names.some((name) => g.namen.includes(name))).reduce((s, l) => s + l.rest, 0); const min = g.minVoorraad; return { cat: "Hulpstof", key: `Hulpstof|${g.naam}`, naam: g.naam, artikelnr: g.artikelnr, leverancier: g.leverancier, type: g.type, voorraad: vr, min, laag: min > 0 && vr < min }; });
  return { grond, hulp };
}